import { TableColumnsType } from "antd";
import VLTable from "views/uikit/VLTable";
import styles from "./style.module.scss";
import TextWithTooltip from "views/components/TextWithTooltip";
import { VLTableActionsConfig } from "views/uikit/VLTable/tableUtils";
import { capitalize } from "lodash";
import { UserData as ModalUserData } from "../UserModalManager/ChangeUserRoleModal";
import { UserModalType, UserRole } from "helpers/constants/users";
import type { WorkspaceMember } from "./useUserTable";
import classNames from "classnames";

type UserData = WorkspaceMember;

interface UserTableProps {
  openModal: (type: UserModalType, user?: ModalUserData) => void;
  loading: boolean;
  tableData: UserData[];
  currentUserId?: string;
  canManage: boolean; // Whether user can manage (admin only)
  className?: string;
}

const UsersTable = ({
  openModal,
  loading,
  tableData,
  currentUserId,
  canManage,
  className,
}: UserTableProps) => {
  const getRandomAvatarColor = (username: string) => {
    const colors = [
      "#7C3AED", // Purple
      "#3B82F6", // Blue
      "#F59E0B", // Orange
      "#EF4444", // Red
      "#10B981", // Green
      "#EC4899", // Pink
      "#8B5CF6", // Violet
      "#06B6D4", // Cyan
      "#F97316", // Orange Red
      "#6366F1", // Indigo
    ];

    // Generate a consistent index based on the username
    let hash = 0;
    for (let i = 0; i < username.length; i++) {
      hash = username.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const allColumns: (TableColumnsType<UserData>[number] & {
    show?: boolean;
  })[] = [
    {
      title: "",
      width: 15,
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 250,
      render: (_, record: UserData) => (
        <div className={styles.usernameCell}>
          <div
            className={styles.avatar}
            style={{ backgroundColor: getRandomAvatarColor(record.username) }}
          >
            {record.username?.[0]?.toUpperCase()}
          </div>
          <TextWithTooltip text={record.username} />
        </div>
      ),
    },
    {
      title: "First Name",
      dataIndex: "firstName",
      key: "firstName",
      width: 150,
      render: (firstName: string | null | undefined) => (
        <TextWithTooltip text={firstName || "-"} />
      ),
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      key: "lastName",
      width: 150,
      render: (lastName: string | null | undefined) => (
        <TextWithTooltip text={lastName || "-"} />
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 150,
      render: (role?: string) => (
        <div className={styles.roleTag}>{role ? capitalize(role) : "-"}</div>
      ),
    },
  ];

  const handleTableActions = (record: UserData, actionType: UserModalType) => {
    const modalUser: ModalUserData = {
      id: record.user_id,
      username: record.username,
      role: (record.role
        ? capitalize(record.role)
        : UserRole.Viewer) as UserRole,
    };

    openModal(actionType, modalUser);
  };

  const columns = allColumns.filter((col) => col.show !== false);

  const actionsConfig: VLTableActionsConfig<UserData> = {
    items: (record) => {
      const isCurrentUser = record.user_id === currentUserId;

      const actions = [
        {
          key: "reset-password",
          label: "Reset Password",
          onClick: () =>
            handleTableActions(record, UserModalType.CHANGE_PASSWORD),
        },
      ];

      // Hide "Change Role" and "Delete User" for current user
      if (!isCurrentUser) {
        actions.push({
          key: "change-role",
          label: "Change Role",
          onClick: () => handleTableActions(record, UserModalType.CHANGE_ROLE),
        });
        actions.push({
          key: "delete-user",
          label: <span className={styles.deleteUserButton}>Delete User</span>,
          onClick: () => handleTableActions(record, UserModalType.DELETE_USER),
          className: styles.deleteUserButton,
        } as any);
      }

      return actions;
    },
  };

  return (
    <div className={classNames(styles.userTableContainer, className)}>
      <div className={styles.title}>
        {canManage ? "Manage Users" : "Workspace Members"}
      </div>
      <VLTable<UserData>
        columns={columns}
        dataSource={tableData}
        loading={loading}
        rowKey="user_id"
        showHeader={true}
        className={styles.userTable}
        actions={canManage ? actionsConfig : undefined}
      />
    </div>
  );
};

export default UsersTable;
